/**
 * Supabase Service
 * Handles bill sessions storage in Supabase cloud database
 */

import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

let supabase = null;

// Initialize Supabase client only if credentials are provided
if (supabaseUrl && supabaseKey && !supabaseUrl.includes('dummy')) {
  supabase = createClient(supabaseUrl, supabaseKey);
}

/**
 * Check if Supabase is configured
 */
export const isSupabaseConfigured = () => {
  return supabase !== null;
};

/**
 * Save a session to Supabase
 */
export const saveSessionToCloud = async (sessionId, billData) => {
  if (!supabase) {
    console.warn('Supabase not configured. Session will only be stored locally.');
    return { success: false, error: 'Supabase not configured' };
  }

  try {
    const { data, error } = await supabase
      .from('bill_sessions')
      .upsert({
        session_id: sessionId,
        bill_data: billData,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() // 24 hours
      }, {
        onConflict: 'session_id'
      });

    if (error) throw error;

    return { success: true, data };
  } catch (error) {
    console.error('Error saving session to Supabase:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get a session from Supabase
 */
export const getSessionFromCloud = async (sessionId) => {
  if (!supabase) {
    return { success: false, error: 'Supabase not configured' };
  }

  try {
    const { data, error } = await supabase
      .from('bill_sessions')
      .select('*')
      .eq('session_id', sessionId)
      .single();

    if (error) throw error;

    // Check if session has expired
    if (data && new Date(data.expires_at) < new Date()) {
      await deleteSessionFromCloud(sessionId);
      return { success: false, error: 'Session expired' };
    }

    return { success: true, data: data.bill_data };
  } catch (error) {
    console.error('Error getting session from Supabase:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete a session from Supabase
 */
export const deleteSessionFromCloud = async (sessionId) => {
  if (!supabase) {
    return { success: false, error: 'Supabase not configured' };
  }

  try {
    const { error } = await supabase
      .from('bill_sessions')
      .delete()
      .eq('session_id', sessionId);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Error deleting session from Supabase:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update session participants
 */
export const updateSessionParticipants = async (sessionId, participants) => {
  if (!supabase) {
    return { success: false, error: 'Supabase not configured' };
  }

  try {
    // Get current session
    const { data: session, error: fetchError } = await supabase
      .from('bill_sessions')
      .select('bill_data')
      .eq('session_id', sessionId)
      .single();

    if (fetchError) throw fetchError;

    // Update participants
    const updatedBillData = {
      ...session.bill_data,
      participants
    };

    const { error: updateError } = await supabase
      .from('bill_sessions')
      .update({
        bill_data: updatedBillData,
        updated_at: new Date().toISOString()
      })
      .eq('session_id', sessionId);

    if (updateError) throw updateError;

    return { success: true };
  } catch (error) {
    console.error('Error updating participants:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update user claims without overwriting others' claims
 * Merges the current user's claims with existing claims from other users
 */
export const updateUserClaims = async (sessionId, userId, billItems) => {
  if (!supabase) {
    console.warn('Supabase not configured.');
    return { success: false, error: 'Supabase not configured' };
  }

  try {
    // First, get the current session data
    const { data: currentSession, error: fetchError } = await supabase
      .from('bill_sessions')
      .select('bill_data')
      .eq('session_id', sessionId)
      .single();

    if (fetchError) throw fetchError;

    const currentBillData = currentSession.bill_data;
    console.log('📥 Current data from Supabase:', currentBillData);

    // Merge claims: Keep claims from other users, update/add this user's claims
    const mergedItems = currentBillData.items.map(currentItem => {
      // Find the corresponding item in the new billItems
      const newItem = billItems.find(item => item.id === currentItem.id);
      
      if (!newItem) {
        // Item not in new data, keep as is
        console.log(`⏭️ Item "${currentItem.name}" not in new data, keeping as is`);
        return currentItem;
      }

      // Filter out old claims from this user
      const otherUsersClaims = (currentItem.claims || []).filter(claim => claim.userId !== userId);
      
      // Get this user's new claims
      const thisUserClaims = (newItem.claims || []).filter(claim => claim.userId === userId);
      
      // Merge: other users' claims + this user's new claims
      const mergedClaims = [...otherUsersClaims, ...thisUserClaims];

      console.log(`🔄 Item "${currentItem.name}":`, {
        itemId: currentItem.id,
        userId: userId,
        oldClaims: currentItem.claims,
        newItemClaims: newItem.claims,
        otherUsersClaims,
        thisUserClaims,
        mergedClaims
      });

      // Return the item with merged claims, keeping original properties
      return {
        ...currentItem,  // Keep all original properties
        claims: mergedClaims  // Update only the claims
      };
    });

    // Update the session with merged data
    const updatedBillData = {
      ...currentBillData,
      items: mergedItems
    };

    console.log('💾 Saving merged data:', updatedBillData);

    const { data, error } = await supabase
      .from('bill_sessions')
      .update({
        bill_data: updatedBillData,
        updated_at: new Date().toISOString()
      })
      .eq('session_id', sessionId);

    if (error) throw error;

    return { success: true, data: updatedBillData };
  } catch (error) {
    console.error('❌ Error updating user claims:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Subscribe to session changes in real-time
 * Returns an unsubscribe function
 */
export const subscribeToSession = (sessionId, callback) => {
  if (!supabase) {
    console.warn('Supabase not configured. Real-time updates disabled.');
    return () => {};
  }

  console.log('📡 Setting up real-time subscription for session:', sessionId);

  const channel = supabase
    .channel(`session-${sessionId}`)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'bill_sessions',
        filter: `session_id=eq.${sessionId}`
      },
      (payload) => {
        console.log('📡 Real-time event received!');
        console.log('📡 Payload:', payload);
        console.log('📡 Event type:', payload.eventType);
        console.log('📡 New data:', payload.new);
        console.log('📡 Old data:', payload.old);
        
        if (payload.new && payload.new.bill_data) {
          console.log('📡 Calling callback with new bill_data');
          callback(payload.new.bill_data);
        } else {
          console.warn('📡 No bill_data in payload');
        }
      }
    )
    .subscribe((status) => {
      console.log('📡 Subscription status:', status);
    });

  // Return unsubscribe function
  return () => {
    console.log('📡 Removing channel subscription');
    supabase.removeChannel(channel);
  };
};

// =====================================================
// FUNCIONES PARA TABLA user_claims (CLAIMS INDIVIDUALES)
// =====================================================

/**
 * Crear registro inicial de usuario en user_claims
 * @param {string} sessionId - ID de la sesión
 * @param {string} userId - ID del usuario
 * @param {string} userName - Nombre del usuario
 */
export const createInitialUserRecord = async (sessionId, userId, userName) => {
  if (!supabase) {
    console.warn('Supabase not configured');
    return { success: false, error: 'Supabase not configured' };
  }

  try {
    console.log('👤 Creating initial user record:', { sessionId, userId, userName });

    // Crear un registro "vacío" solo para registrar al usuario
    // Usamos un item_id especial para indicar que es solo registro de usuario
    const { data, error } = await supabase
      .from('user_claims')
      .insert({
        session_id: sessionId,
        user_id: userId,
        user_name: userName,
        item_id: '__USER_INIT__',
        item_name: 'User Registration',
        quantity: 0,
        unit_price: 0,
        total_price: 0,
        status: 'registered'
      });

    if (error) throw error;

    console.log('✅ Initial user record created');
    return { success: true, data };

  } catch (error) {
    console.error('❌ Error creating initial user record:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Guardar o actualizar un claim de usuario para un item específico
 * @param {string} sessionId - ID de la sesión
 * @param {string} userId - ID del usuario
 * @param {string} userName - Nombre del usuario
 * @param {string} itemId - ID del item
 * @param {string} itemName - Nombre del item
 * @param {number} quantity - Cantidad reclamada
 * @param {number} unitPrice - Precio unitario
 */
export const saveUserClaim = async (sessionId, userId, userName, itemId, itemName, quantity, unitPrice) => {
  if (!supabase) {
    console.warn('Supabase not configured');
    return { success: false, error: 'Supabase not configured' };
  }

  try {
    // Ensure quantity is numeric (allow fractional quantities for shared items)
    const qty = typeof quantity === 'string' ? parseFloat(quantity) : quantity;
    const sanitizedQty = Number.isFinite(qty) ? Math.round(qty * 100) / 100 : 0; // 2 decimals
    const totalPrice = Number(unitPrice) * sanitizedQty;

    console.log('💾 Saving user claim:', { sessionId, userId, userName, itemId, itemName, quantity, unitPrice, totalPrice });

    // Buscar si ya existe un claim para este usuario e item
    const { data: existing, error: searchError } = await supabase
      .from('user_claims')
      .select('*')
      .eq('session_id', sessionId)
      .eq('user_id', userId)
      .eq('item_id', itemId)
      .single();

    if (searchError && searchError.code !== 'PGRST116') { // PGRST116 = no rows found
      throw searchError;
    }

    if (existing) {
      // Actualizar claim existente
      if (quantity === 0) {
        // Si quantity es 0, eliminar el claim
        const { error: deleteError } = await supabase
          .from('user_claims')
          .delete()
          .eq('id', existing.id);

        if (deleteError) throw deleteError;
        console.log('🗑️ Deleted claim:', existing.id);
        
        // WORKAROUND: Crear y eliminar un registro dummy para forzar broadcast
        // Esto es necesario porque Supabase real-time DELETE no siempre funciona
        const dummyId = `__DELETED_${existing.id}_${Date.now()}`;
        await supabase.from('user_claims').insert({
          session_id: sessionId,
          user_id: userId,
          user_name: userName,
          item_id: dummyId,
          item_name: '__TRIGGER__',
          quantity: 0,
          unit_price: 0,
          total_price: 0,
          status: 'trigger'
        });
        await supabase.from('user_claims').delete().eq('item_id', dummyId);
        
        return { success: true, action: 'deleted' };
      } else {
        // Actualizar cantidad y precio
        const { data, error: updateError } = await supabase
          .from('user_claims')
          .update({
            quantity: sanitizedQty,
            unit_price: unitPrice,
            total_price: totalPrice,
            updated_at: new Date().toISOString()
          })
          .eq('id', existing.id)
          .select();

        if (updateError) {
          // If the DB rejects decimal quantities (older schema), retry rounding to integer
          console.warn('Update claim failed, retrying with integer quantity if possible', updateError);
          if (updateError?.code === '22P02' || /invalid input syntax for type integer/.test(updateError?.message || '')) {
            const intQty = Math.round(sanitizedQty);
            const { data: retryData, error: retryErr } = await supabase
              .from('user_claims')
              .update({
                quantity: intQty,
                unit_price: unitPrice,
                total_price: Number(unitPrice) * intQty,
                updated_at: new Date().toISOString()
              })
              .eq('id', existing.id)
              .select();
            if (retryErr) throw retryErr;
            console.log('✅ Updated claim after rounding quantity to integer:', retryData);
            return { success: true, action: 'updated', data: retryData[0] };
          }
          throw updateError;
        }
        console.log('✅ Updated claim:', data);
        return { success: true, action: 'updated', data: data[0] };
      }
    } else {
      // Crear nuevo claim
      if (quantity === 0) {
        // No crear claim si quantity es 0
        return { success: true, action: 'skipped' };
      }

      const { data, error: insertError } = await supabase
        .from('user_claims')
        .insert({
          session_id: sessionId,
          user_id: userId,
          user_name: userName,
          item_id: itemId,
          item_name: itemName,
          quantity: sanitizedQty,
          unit_price: unitPrice,
          total_price: totalPrice,
          status: 'claimed'
        })
        .select();

      if (insertError) {
        // Retry insert with rounded integer quantity if DB expects integer
        console.warn('Insert claim failed, attempting integer fallback', insertError);
        if (insertError?.code === '22P02' || /invalid input syntax for type integer/.test(insertError?.message || '')) {
          const intQty = Math.round(sanitizedQty);
          const { data: retryData, error: retryErr } = await supabase
            .from('user_claims')
            .insert({
              session_id: sessionId,
              user_id: userId,
              user_name: userName,
              item_id: itemId,
              item_name: itemName,
              quantity: intQty,
              unit_price: unitPrice,
              total_price: Number(unitPrice) * intQty,
              status: 'claimed'
            })
            .select();
          if (retryErr) throw retryErr;
          console.log('✅ Created new claim after rounding quantity to integer:', retryData);
          return { success: true, action: 'created', data: retryData[0] };
        }
        throw insertError;
      }
      console.log('✅ Created new claim:', data);
      return { success: true, action: 'created', data: data[0] };
    }
  } catch (error) {
    console.error('❌ Error saving user claim:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Obtener todos los claims de un usuario en una sesión
 */
export const getUserClaims = async (sessionId, userId) => {
  if (!supabase) {
    return { success: false, error: 'Supabase not configured' };
  }

  try {
    const { data, error } = await supabase
      .from('user_claims')
      .select('*')
      .eq('session_id', sessionId)
      .eq('user_id', userId)
      .order('created_at', { ascending: true });

    if (error) throw error;

    console.log(`📥 Loaded ${data?.length || 0} claims for user ${userId}`);
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('❌ Error getting user claims:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Obtener todos los claims de una sesión (todos los usuarios)
 */
export const getAllSessionClaims = async (sessionId) => {
  if (!supabase) {
    return { success: false, error: 'Supabase not configured' };
  }

  try {
    const { data, error } = await supabase
      .from('user_claims')
      .select('*')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true });

    if (error) throw error;

    console.log(`📥 Loaded ${data?.length || 0} total claims for session ${sessionId}`);
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('❌ Error getting session claims:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Actualizar el estado de un claim (claimed -> paid -> disputed)
 */
export const updateClaimStatus = async (claimId, newStatus) => {
  if (!supabase) {
    return { success: false, error: 'Supabase not configured' };
  }

  try {
    const { data, error } = await supabase
      .from('user_claims')
      .update({
        status: newStatus,
        updated_at: new Date().toISOString()
      })
      .eq('id', claimId)
      .select();

    if (error) throw error;

    console.log('✅ Updated claim status:', data);
    return { success: true, data: data[0] };
  } catch (error) {
    console.error('❌ Error updating claim status:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Suscribirse a cambios en los claims de una sesión
 */
export const subscribeToSessionClaims = (sessionId, callback) => {
  if (!supabase) {
    console.warn('Supabase not configured. Real-time updates disabled.');
    return () => {};
  }

  console.log('📡 Setting up real-time subscription for claims:', sessionId);

  const channel = supabase
    .channel(`claims-${sessionId}-${Date.now()}`) // Unique channel per mount
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'user_claims',
        filter: `session_id=eq.${sessionId}`
      },
      (payload) => {
        console.log('📡 Claims INSERT event received');
        callback(payload);
      }
    )
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'user_claims',
        filter: `session_id=eq.${sessionId}`
      },
      (payload) => {
        console.log('📡 Claims UPDATE event received');
        callback(payload);
      }
    )
    .on(
      'postgres_changes',
      {
        event: 'DELETE',
        schema: 'public',
        table: 'user_claims',
        filter: `session_id=eq.${sessionId}`
      },
      (payload) => {
        console.log('📡 Claims DELETE event received');
        callback(payload);
      }
    )
    .subscribe((status, err) => {
      if (err) {
        console.error('❌ Claims subscription error:', err);
      } else {
        console.log('📡 Claims subscription status:', status);
      }
    });

  return () => {
    console.log('📡 Removing claims channel subscription');
    supabase.removeChannel(channel);
  };
};
